/* eslint-disable no-param-reassign */
import { Draft } from 'immer';

import { MAX_ZOOM_IN, MAX_ZOOM_OUT, STAGE_SIDE_PADDING } from './constants';
import { DiffUIState } from './types';
import { ImageType } from '../../global/constants';

/**
 * Constrain a zoom value to the min / max allowed values.
 * @param zoom
 */
export const constrainZoom = (zoom: number) => {
  if (zoom <= MAX_ZOOM_OUT) {
    return Math.max(zoom, MAX_ZOOM_OUT);
  } if (zoom >= MAX_ZOOM_IN) {
    return Math.min(zoom, MAX_ZOOM_IN);
  }

  return zoom;
};

/**
 * Takes the stage in a writable draft and zooms it by the requested value while maintaining the
 * offset (visible stage center).
 * @param state
 * @param zoom
 */
export const zoomToCenter = (state: Draft<DiffUIState>, zoom: number) => {
  const oldZoom = state.zoom;

  // Find center of stage
  const center = {
    x: state.stageDims.width / 2,
    y: state.stageDims.height / 2,
  };

  // get the old offset
  const modifiedOffset = {
    x: (center.x - state.offset.x) / oldZoom,
    y: (center.y - state.offset.y) / oldZoom,
  };

  // Apply new offsets
  state.zoom = constrainZoom(zoom);
  state.offset = {
    x: center.x - modifiedOffset.x * state.zoom,
    y: center.y - modifiedOffset.y * state.zoom,
  };
};

/**
 * Calculates the usable stage area for height + width based on our spacing.
 * @param dims
 */
export const getUsableStageArea = (dims: {width: number, height: number}) => {
  const { width, height } = dims;

  const xSpacing = width -
    // Left & right padding on stage itself
    (STAGE_SIDE_PADDING * 2) -
    // Additionally, width of the toolbar plus an additional side padding to the left
    (40 + STAGE_SIDE_PADDING);

  const ySpacing = height -
    // Top & bottom spacing
    STAGE_SIDE_PADDING * 2;

  return {
    width: xSpacing,
    height: ySpacing,
  };
};

/**
 * Resize an item [child] into a parent container [container]. Returns the new dims of the child.
 * @param child The child we should resize.
 * @param container The container we should constrain the child to.
 * @param opts
 */
export const fit = (
  child: {width: number, height: number},
  container: {width: number, height: number},
  opts: { enlarge?: boolean } = { enlarge: true },
) => {
  const { enlarge = false } = opts;
  const newDims = { ...child };
  const childAspectRatio = child.width / child.height;
  const containerAspectRatio = container.width / container.height;
  const constrainedDim = containerAspectRatio <= childAspectRatio ? 'width' : 'height';
  const otherDim = constrainedDim === 'height' ? 'width' : 'height';
  const resizeRatio = child[constrainedDim] / container[constrainedDim];

  newDims[constrainedDim] = container[constrainedDim];
  newDims[otherDim] = child[otherDim] / resizeRatio;

  if (
    (newDims.width > child.width || newDims.height > child.width) &&
    !enlarge
  ) {
    return child;
  }

  return newDims;
};

/**
 * Zooms and adjusts the offset to fit the largest image in state into view (adding our padding
 * / spacing) & center it.
 *
 * @param state
 * @param enlarge
 */
export const zoomToFitStage = (state: Draft<DiffUIState>, enlarge = false) => {
// Add extra padding to account for floating sidebar
  const usableStageArea = getUsableStageArea(state.stageDims);
  const { height: stageHeight, width: stageWidth } = usableStageArea;

  let image: {height: number, width: number} | undefined;

  const baselineDims = state.dims[ImageType.BASELINE];
  const snapshotDims = state.dims[ImageType.SNAPSHOT];

  if (baselineDims.height && baselineDims.width) {
    image = {
      width: baselineDims.width,
      height: baselineDims.height,
    };
  } else if (snapshotDims.height && snapshotDims.width) {
    image = {
      width: snapshotDims.width,
      height: snapshotDims.height,
    };
  }

  if (!image) {
    return;
  }

  const newImageDims = fit(image, {
    height: stageHeight,
    width: stageWidth,
  }, { enlarge });

  const resizeRatio = newImageDims.width / image.width;

  // Then subtract padding and fit to screen
  state.zoom = constrainZoom(resizeRatio);
  state.offset = {
    x: (stageWidth / 2) - (newImageDims.width / 2) + STAGE_SIDE_PADDING,
    y: (stageHeight / 2) - (newImageDims.height / 2) + STAGE_SIDE_PADDING,
  };
};
